import { BaseError } from "@/domains/errors";
import { api } from "@/modules/api";
import { normalizeFilename } from "@/modules/file/normalizeFileName";
import { resizeProfilePhotoFile } from "@/modules/file/profilePhotoImageFile";
import { uuidModule } from "@/modules/uuid";

const PUBLIC_UPLOADS_SIGNED_URL_PATH = "/v2/file-uploads/generate-signed-url-for-public-uploads";

export interface UploadProfilePhotoResult {
  uploadedFileId: string;
  uploadedFileUrl: string;
}

export const uploadProfilePhoto = async ({
  file,
}: {
  file: File;
}): Promise<UploadProfilePhotoResult> => {
  const resizedFile = await resizeProfilePhotoFile(file);
  const fileId = uuidModule.generate();
  const normalizedFilename = normalizeFilename({ fileName: resizedFile.name });
  const signedUrl = await generatePublicUploadsSignedUrl({
    fileId,
    file: resizedFile,
    normalizedFilename,
  });

  const uploadResponse = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": resizedFile.type,
    },
    body: resizedFile,
  });

  if (!uploadResponse.ok) {
    throw new BaseError({ message: "[uploadProfilePhoto] Failed to upload profile photo" });
  }

  const uploadedFileUrl = signedUrl.split("?")[0];
  const registerResponse = await api.post("/v2/uploaded-files", {
    body: {
      id: fileId,
      filename: normalizedFilename,
      mime_type: resizedFile.type,
      file_size_bytes: resizedFile.size,
      uploaded_file_url: uploadedFileUrl,
    },
  });

  if (registerResponse.error || !registerResponse.data) {
    throw new BaseError({
      message: "[uploadProfilePhoto] Failed to register profile photo",
      info: { result: registerResponse },
    });
  }

  return {
    uploadedFileId: registerResponse.data.id,
    uploadedFileUrl,
  };
};

const generatePublicUploadsSignedUrl = async ({
  fileId,
  file,
  normalizedFilename,
}: {
  fileId: string;
  file: File;
  normalizedFilename: string;
}): Promise<string> => {
  const signedUrlPath = PUBLIC_UPLOADS_SIGNED_URL_PATH as "/v2/file-uploads/generate-signed-url";

  // The request/response shape matches the default signed-url route; remove
  // this path cast once mem-client codegen includes the public-uploads route.
  const response = await api.post(signedUrlPath, {
    body: {
      file_id: fileId,
      file_name: normalizedFilename,
      file_mime_type: file.type,
    },
  });

  if (response.error || !response.data) {
    throw new BaseError({
      message: "[uploadProfilePhoto] Failed to generate public upload signed URL",
      info: { result: response },
    });
  }

  return response.data.signed_url;
};
